import React, { useState } from 'react';
import { Share2, Linkedin, Twitter, Copy, Check, ExternalLink } from 'lucide-react';
import { generateShareContent, generateLinkedInShareUrl, generateTwitterShareUrl, copyToClipboard, openShareWindow } from '../lib/utils/sharing';
import { useToast } from '../hooks/useToast';
import type { InteractionMessage } from '../types/interactions';

interface SocialShareProps {
  message: InteractionMessage;
  onShare: (messageId: string, platform: string) => Promise<{ success: boolean; error: string | null }>;
  className?: string;
}

export default function SocialShare({ message, onShare, className = '' }: SocialShareProps) {
  const [isSharing, setIsSharing] = useState(false);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const { success, error: showError } = useToast();

  // Only show share buttons for messages with images
  if (!message.has_image || !message.image_url) {
    return null;
  }

  const handleShare = async (platform: 'linkedin' | 'twitter' | 'copy') => {
    setIsSharing(true);

    try {
      const shareContent = generateShareContent(
        message.message_content,
        message.response_received ? message.response_received_at || undefined : undefined
      );

      if (platform === 'copy') {
        const shareText = `${shareContent.text}\n\n${shareContent.url}`;
        const copied = await copyToClipboard(shareText);
        
        if (copied) {
          setCopiedToClipboard(true);
          setTimeout(() => setCopiedToClipboard(false), 2000);
          success('Copied!', 'Share text copied to clipboard');
        } else {
          showError('Copy Failed', 'Unable to copy to clipboard');
          return;
        }
      } else {
        const shareUrl = platform === 'linkedin' 
          ? generateLinkedInShareUrl(shareContent)
          : generateTwitterShareUrl(shareContent);
        
        openShareWindow(shareUrl, platform);
      }

      // Record the share
      const result = await onShare(message.id, platform);
      if (!result.success && !message.id.startsWith('sample-')) {
        showError('Share Error', result.error || 'Failed to record share');
      }

    } catch (err) {
      showError('Share Error', 'Failed to share content');
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <div className={`flex items-center justify-between ${className}`}>
      <div className="flex items-center space-x-3">
        <div className="flex items-center space-x-1 text-sm text-gray-500">
          <Share2 className="h-4 w-4" />
          <span>Share your progress</span>
        </div>
        
        <div className="flex items-center space-x-1">
          <button
            onClick={() => handleShare('linkedin')}
            disabled={isSharing}
            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1"
            aria-label="Share on LinkedIn"
            title="Share on LinkedIn"
          >
            <Linkedin className="h-4 w-4" />
          </button>
          
          <button
            onClick={() => handleShare('twitter')}
            disabled={isSharing}
            className="p-2 text-gray-600 hover:text-blue-400 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1"
            aria-label="Share on Twitter"
            title="Share on Twitter"
          >
            <Twitter className="h-4 w-4" />
          </button>
          
          <button
            onClick={() => handleShare('copy')}
            disabled={isSharing}
            className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1"
            aria-label="Copy share link"
            title="Copy share link"
          >
            {copiedToClipboard ? (
              <Check className="h-4 w-4 text-emerald-600" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
      
      <div className="flex items-center space-x-2 text-xs text-gray-500">
        {message.share_count > 0 && (
          <span>
            {message.share_count} {message.share_count === 1 ? 'share' : 'shares'}
          </span>
        )}
        <ExternalLink className="h-3 w-3" />
      </div>
    </div>
  );
}